import { isEmpty, merge } from "lodash";

// Filter components to only include those that match the given category
const matchToCategory = (componentTypeObj = {}, catId = "", availableCategories = []) => {
    return Object.entries(componentTypeObj)
        .filter(([componentId, componentData]) => {
            const { category = "" } = componentData || {};

            return (
                componentData &&
                !isEmpty(componentData) &&
                (category === catId || (catId === "other" && !availableCategories.includes(category)))
            );
        })
};

// Filter components to only include those that match the given category and room key
const matchToRoom = (componentTypeObj = {}, catId = "", roomKey = "", availableCategories = []) => {
    return Object.entries(componentTypeObj)
        .filter(([componentId, componentData]) => {
            const { category = "", roomId = "" } = componentData || {};

            return (
                componentData &&
                !isEmpty(componentData) &&
                (category === catId || (catId === "other" && !availableCategories.includes(category))) &&
                (roomId === roomKey || (roomKey === `room-default-${catId}` && (!roomId || roomId === `room-default-${category}`)))
            );
        })
};

// Filter subcomponents to only include those that have data
const matchSubToValues = (subComponentTypeObj = {}) => {
    return Object.entries(subComponentTypeObj)
        .filter(([componentId, componentData]) => componentData && !isEmpty(componentData));
};

// Summarize component information
const summarizeComponentInfo = (componentType = "", componentId = "", componentData = {}, includeData = false, parentPath = "", parentInfo = {}) => {
    const { label = "", displayOrder = 0, subcomponents = {}, isInterior = false } = componentData;

    const hasSubcomponents = !isEmpty(subcomponents);

    const componentPath = parentPath
        ? `${parentPath}.subcomponents.${componentType}.${componentId}`
        : `${componentType}.${componentId}`;

    return {
        id: componentId,
        componentId: componentId,
        componentType: componentType,
        componentLabel: label,
        displayOrder,
        ...parentInfo,
        ...(isInterior ? { isInterior } : {}),
        ...(hasSubcomponents ? { subcomponents: getCatRoomSubComponents(subcomponents, includeData, componentPath, isInterior) } : {}),
        ...(includeData ? { data: componentData } : {}),
    }
}

// Sort components by display order or label
const sortComponents = (components = []) => {
    return components.sort((a, b) => {
        if (a.displayOrder !== b.displayOrder) {
            return a.displayOrder - b.displayOrder;
        }
        // update
        if (a.componentLabel !== b.componentLabel && a.componentLabel != null && b.componentLabel !== null) {
            if (a.componentLabel?.toLowerCase() < b.componentLabel?.toLowerCase()) {
                return -1;
            }
            if (a.componentLabel?.toLowerCase() > b.componentLabel?.toLowerCase()) {
                return 1;
            }
            return 0;
        }
        return 0;
    });
};

// Return a summarized list of subcomponents from a subcomponents object
export const getCatRoomSubComponents = (subcomponentsObject = {}, includeData = false, parentPath = "", isInterior = false) => {
    const { window = {}, door = {}, floorHeader = {} } = subcomponentsObject;

    const parentInfo = parentPath
        ? {
            parentAccessor: isInterior
                ? `model.modelData.intComponents.${parentPath}`
                : `model.modelData.components.${parentPath}`,
            parentPath: `.${parentPath}.subcomponents`,
            isInterior,
        }
        : {};

    const windows = matchSubToValues(window)
        .map(([componentId, componentData]) => summarizeComponentInfo("window", componentId, componentData, includeData, parentPath, parentInfo));

    const doors = matchSubToValues(door)
        .map(([componentId, componentData]) => summarizeComponentInfo("door", componentId, componentData, includeData, parentPath, parentInfo));

    const floorHeaders = matchSubToValues(floorHeader)
        .map(([componentId, componentData]) => summarizeComponentInfo("floorHeader", componentId, componentData, includeData, parentPath, parentInfo));

    return [...windows, ...doors, ...floorHeaders];
};

// Return a sorted summarized list of components within a particular storey room from a components object
export const getCatRoomComponents = (componentsObject = {}, catId = "", roomKey = "", availableCategories = [], includeData = false) => {
    const { basement = {}, crawlspace = {}, slab = {}, wall = {}, ceiling = {}, expFloor = {} } = componentsObject;

    const basements = matchToRoom(basement, catId, roomKey, availableCategories)
        .map(([componentId, componentData]) => summarizeComponentInfo("basement", componentId, componentData, includeData));

    const crawlspaces = matchToRoom(crawlspace, catId, roomKey, availableCategories)
        .map(([componentId, componentData]) => summarizeComponentInfo("crawlspace", componentId, componentData, includeData));

    const slabs = matchToRoom(slab, catId, roomKey, availableCategories)
        .map(([componentId, componentData]) => summarizeComponentInfo("slab", componentId, componentData, includeData));

    const walls = matchToRoom(wall, catId, roomKey, availableCategories)
        .map(([componentId, componentData]) => summarizeComponentInfo("wall", componentId, componentData, includeData));

    const ceilings = matchToRoom(ceiling, catId, roomKey, availableCategories)
        .map(([componentId, componentData]) => summarizeComponentInfo("ceiling", componentId, componentData, includeData));

    const expFloors = matchToRoom(expFloor, catId, roomKey, availableCategories)
        .map(([componentId, componentData]) => summarizeComponentInfo("expFloor", componentId, componentData, includeData));

    return sortComponents([...slabs, ...crawlspaces, ...basements, ...ceilings, ...walls, ...expFloors]);
};

// Return a sorted list of components within a particular storey from a components object
export const getCatComponents = (componentsObject = {}, catId = "", availableCategories = [], includeData = false) => {
    const { basement = {}, crawlspace = {}, slab = {}, wall = {}, ceiling = {}, expFloor = {} } = componentsObject;

    const basements = matchToCategory(basement, catId, availableCategories)
        .map(([componentId, componentData]) => summarizeComponentInfo("basement", componentId, componentData, includeData));

    const crawlspaces = matchToCategory(crawlspace, catId, availableCategories)
        .map(([componentId, componentData]) => summarizeComponentInfo("crawlspace", componentId, componentData, includeData));

    const slabs = matchToCategory(slab, catId, availableCategories)
        .map(([componentId, componentData]) => summarizeComponentInfo("slab", componentId, componentData, includeData));

    const walls = matchToCategory(wall, catId, availableCategories)
        .map(([componentId, componentData]) => summarizeComponentInfo("wall", componentId, componentData, includeData));

    const ceilings = matchToCategory(ceiling, catId, availableCategories)
        .map(([componentId, componentData]) => summarizeComponentInfo("ceiling", componentId, componentData, includeData));

    const expFloors = matchToCategory(expFloor, catId, availableCategories)
        .map(([componentId, componentData]) => summarizeComponentInfo("expFloor", componentId, componentData, includeData));

    return sortComponents([...slabs, ...crawlspaces, ...basements, ...ceilings, ...walls, ...expFloors]);
};

// Return the next display order in a list of components
export const getNextComponentDisplayOrder = (components = []) => {
    const lastDisplayOrder = components.reduce(
        ({ displayOrder: prevDisplay = 0 }, { displayOrder: currentDisplay = 0 }) =>
            prevDisplay > currentDisplay ? prevDisplay : currentDisplay,
        0
    );
    return lastDisplayOrder + 1;
};

// Return a sorted list of components by room
export const getComponentsSortedByRoom = (components) => Object.entries(components).reduce((acc, [componentType, componentList]) => {
    const componentsOfType = Object.entries(componentList).reduce((acc, [componentKey, componentData]) => {
        return {
            ...acc,
            [componentData.category]: {
                ...acc[componentData.category],
                [componentData.roomId]: {
                    ...(acc[componentData.category]?.[componentData.roomId] || {}),
                    [componentKey]: {
                        ...componentData,
                        type: componentType,
                    },
                },
            },
        };
    }, {});
    return merge(acc, componentsOfType);
}, {});